import React from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import {
  Button,
  Col,
  Form,
  Icon,
  Input,
  Select,
  Radio,
  Row,
  Tooltip,
  message,
} from "antd";
import get from "lodash/get";
import isEmpty from "lodash/isEmpty";
import axios from "axios";
import { STATES } from "config";
import clipboard from "utils/clipboard";

const FormItem = Form.Item;

class CheckoutForm extends React.Component {
  static propTypes = {
    onSubmit: PropTypes.func,
    labData: PropTypes.object,
    saving: PropTypes.bool,
  };
  state = {
    copyText: "",
  };

  onSubmit = (e) => {
    const { form, user, labData } = this.props;
    e.preventDefault();
    form.validateFields((err, values) => {
      if (!err) {
        const {
          shipping,
          orderReference,
          specialMessage,
          deliveryAddress,
          items,
        } = values;
        const ship = labData.shipping.find((s) => s.pos_code === shipping);
        const shipType = ship.shiptype;
        const isDrop = shipType === "dropship";

        const data = {
          shipping,
          orderReference,
          specialMessage,
          deliveryAddress,
          items,
        };

        if (isDrop) {
          data.returnAddress = user.address;
        }
        this.props.onSubmit(data);
      }
    });
  };

  parseAddress = (text) => {
    this.setState({ copyText: text });
    axios
      .post(
        "https://qjn81caq90.execute-api.us-west-2.amazonaws.com/prod/parse-address",
        text
      )
      .then(
        (resp) => {
          const result = get(resp, "0");
          if (result) {
            let fname = "";
            let lname = "";
            if (result.recipient) {
              const nameParts = result.recipient.split(" ");
              if (nameParts.length > 1) {
                fname = nameParts[0];
                lname = nameParts.splice(1).join(" ");
              } else {
                fname = result.recipient;
              }
            }
            const data = {
              firstName: fname,
              lastName: lname,
              line1: result.address1,
              line2: result.address2,
              city: result.city,
              state: result.state,
              zip: result.zip_code,
            };

            this.props.form.setFieldsValue({
              deliveryAddress: data,
            });
          } else {
            message.error("Failed to parse");
          }
        },
        (err) => {
          message.error("Error occurred, please try again.");
        }
      );
  };

  onPasteAddress = () => {
    this.setState({
      copyText: "",
    });
    clipboard(this.parseAddress);
  };

  onPasteJobRef = () => {
    clipboard((text) => {
      this.props.form.setFieldsValue({ orderReference: text });
    });
  };

  calcPrepCost = () => {
    const { cart, mappings, labData } = this.props;
    const { prep } = labData;
    let surcharge = 0;
    const mappingQty = {
      "8x10": 0,
      "11x14": 0,
    };
    Object.keys(cart).forEach((id) => {
      const mapping = mappings.find((i) => i.id === id);
      const { product_size } = mapping;
      mappingQty[product_size] = mappingQty[product_size] + mapping.orderQty;
      if (mapping.orderQty < prep.surchargeQty) {
        surcharge = surcharge + mapping.orderQty;
      }
    });
    const priceLines = [];
    Object.keys(mappingQty).forEach((size) => {
      const qty = mappingQty[size];
      if (qty) {
        const total = prep[size].price * qty;
        priceLines.push(
          <li>
            {size} - {qty} SKU's at ${prep[size].price} each: $
            {total.toFixed(2)}
          </li>
        );
      }
    });
    if (surcharge) {
      const surchargeTotal = surcharge * prep.surchargeFee;
      priceLines.push(
        <li>
          Surcharge - {surcharge} at ${prep.surchargeFee.toFixed(2)} each: $
          {surchargeTotal.toFixed(2)}{" "}
          <Tooltip
            title={`Additional charge if any single Mapping quantity is less than ${
              prep.surchargeQty
            }`}
          >
            <Icon type="info-circle-o" />
          </Tooltip>
        </li>
      );
    }
    return priceLines;
  };

  render() {
    const {
      form: { getFieldDecorator, getFieldValue },
      labData,
      saving,
      user,
    } = this.props;
    const { copyText } = this.state;

    const { shipping } = labData || {};

    const shippingMethod = getFieldValue("shipping");
    const ship = shipping.find((s) => s.pos_code === shippingMethod);
    const shipType = ship ? ship.shiptype : undefined;
    const isStandard = shipType === "ship";
    const isPrep = shippingMethod === "fbqprep";

    return (
      <Form className="checkout-form" onSubmit={this.onSubmit}>
        <FormItem label="Job Reference">
          {getFieldDecorator("orderReference", {})(
            <Input
              placeholder="Optional"
              addonAfter={
                <Tooltip title="Paste from clipboard">
                  <Icon type="snippets" onClick={this.onPasteJobRef} />
                </Tooltip>
              }
            />
          )}
        </FormItem>
        <FormItem label="Special Instructions">
          {getFieldDecorator("specialMessage", {})(
            <Input placeholder="Optional" />
          )}
        </FormItem>
        <h3>Shipping Method</h3>
        <FormItem>
          {getFieldDecorator("shipping", {
            rules: [
              { required: true, message: "Please select shipping method" },
            ],
          })(
            <Radio.Group>
              {shipping.map((s) => (
                <Radio value={s.pos_code}>
                  <span>
                    {s.shippingmethod} - ${s.price}{" "}
                    <Tooltip title={s.description}>
                      <Icon type="info-circle-o" />
                    </Tooltip>
                  </span>
                </Radio>
              ))}
            </Radio.Group>
          )}
        </FormItem>
        {shippingMethod && !isPrep ? (
          <div className="address">
            <div className="d-flex" style={{ marginBottom: ".5rem" }}>
              <h4 className="flex-fill" style={{ marginBottom: 0 }}>
                Delivery Address:
              </h4>
              {!isStandard && (
                <Tooltip title={copyText || "Paste address from clipboard"}>
                  <Button
                    icon="snippets"
                    type="primary"
                    onClick={this.onPasteAddress}
                  />
                </Tooltip>
              )}
            </div>
            <Row gutter={12}>
              <Col span={12}>
                <FormItem>
                  {getFieldDecorator("deliveryAddress.firstName", {
                    initialValue:
                      isStandard && shippingMethod !== "fbqprep"
                        ? get(user, "firstName")
                        : "",
                    rules: [
                      {
                        required: shippingMethod !== "fbqprep" ? true : false,
                        message: "This field is required",
                      },
                    ],
                  })(
                    <Input
                      placeholder="First name / Company name"
                      disabled={isStandard}
                    />
                  )}
                </FormItem>
              </Col>
              <Col span={12}>
                <FormItem>
                  {getFieldDecorator("deliveryAddress.lastName", {
                    initialValue:
                      isStandard && shippingMethod !== "fbqprep"
                        ? get(user, "lastName")
                        : "",
                    rules: [
                      { required: false, message: "This field is required" },
                    ],
                  })(
                    <Input
                      placeholder="Last name / Contact"
                      disabled={isStandard}
                    />
                  )}
                </FormItem>
              </Col>
            </Row>
            <FormItem>
              {getFieldDecorator("deliveryAddress.line1", {
                initialValue:
                  isStandard && shippingMethod !== "fbqprep"
                    ? get(user, "address.line1")
                    : "",
                rules: [
                  {
                    required: shippingMethod !== "fbqprep" ? true : false,
                    message: "This field is required",
                  },
                ],
              })(<Input placeholder="Address line 1" disabled={isStandard} />)}
            </FormItem>
            <FormItem>
              {getFieldDecorator("deliveryAddress.line2", {
                initialValue:
                  isStandard && shippingMethod !== "fbqprep"
                    ? get(user, "address.line2")
                    : "",
                rules: [{ required: false }],
              })(<Input placeholder="Address line 2" disabled={isStandard} />)}
            </FormItem>
            <Row gutter={12}>
              <Col span={8}>
                <FormItem>
                  {getFieldDecorator("deliveryAddress.city", {
                    initialValue:
                      isStandard && shippingMethod !== "fbqprep"
                        ? get(user, "address.city")
                        : "",
                    rules: [
                      {
                        required: shippingMethod !== "fbqprep" ? true : false,
                        message: "This field is required",
                      },
                    ],
                  })(<Input placeholder="City" disabled={isStandard} />)}
                </FormItem>
              </Col>
              <Col span={8}>
                <FormItem>
                  {getFieldDecorator("deliveryAddress.state", {
                    initialValue: isStandard
                      ? get(user, "address.state")
                      : undefined,
                    rules: [
                      { required: true, message: "This field is required" },
                    ],
                  })(
                    <Select
                      disabled={isStandard}
                      showSearch
                      placeholder="Select"
                      optionFilterProp="children"
                    >
                      {STATES.map((s) => (
                        <Select.Option value={s.abbrev} key={s.abbrev}>
                          {s.name}
                        </Select.Option>
                      ))}
                    </Select>
                  )}
                </FormItem>
              </Col>
              <Col span={8}>
                <FormItem>
                  {getFieldDecorator("deliveryAddress.zip", {
                    initialValue: isStandard ? get(user, "address.zip") : "",
                    rules: [
                      { required: true, message: "This field is required" },
                    ],
                  })(<Input placeholder="ZIP" disabled={isStandard} />)}
                </FormItem>
              </Col>
            </Row>
          </div>
        ) : (
          isPrep && (
            <div>
              <h4 className="flex-fill">Prep Cost: {this.calcPrepCost()}</h4>
            </div>
          )
        )}
        {shippingMethod ? (
          <div>
            <Button
              block
              size="large"
              type="primary"
              htmlType="submit"
              loading={saving}
              disabled={isEmpty(this.props.cart)}
            >
              Place Order*
            </Button>
            <p className="padtop">
              *By placing an order you agree to our{" "}
              <a
                rel="noopener noreferrer"
                target="_blank"
                href="https://qprints.com/policies/"
              >
                Terms of Use
              </a>
              .
            </p>
          </div>
        ) : null}
      </Form>
    );
  }
}

export default connect((state) => state.session)(Form.create()(CheckoutForm));
